// Date/time helpers — European (NL), without an external date library.
// Local ISO strings without offset, format "yyyy-MM-ddTHH:mm:ss" (Europe/Amsterdam).

const TZ = "Europe/Amsterdam";

export function pad(n: number): string {
  return String(n).padStart(2, "0");
}

export function toLocalIso(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

export function parseLocal(iso: string): Date {
  // Strip any offset/Z and parse as local time.
  const m = iso.replace(/(\+\d{2}:\d{2}|Z)$/, "").slice(0, 19);
  const [date, time = "00:00:00"] = m.split("T");
  const [y, mo, da] = date.split("-").map(Number);
  const [h, mi, s = 0] = time.split(":").map(Number);
  return new Date(y, mo - 1, da, h, mi, s);
}

// ── Native date/time input values ───────────────────────────────────────────
// The two shapes the browser's own inputs speak: "YYYY-MM-DD" for type="date"
// and "YYYY-MM-DDTHH:mm" for type="datetime-local".
export function toDateInput(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function toDateTimeInput(d: Date): string {
  return `${toDateInput(d)}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const INPUT_SHAPE = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2})?$/;

// Read an input value, or null when it is not a real moment. Round-tripping is
// the check, and it is doing real work: a date input hands over what has been
// typed SO FAR, so "0009-09-11" arrives while somebody types 2009 — and
// `new Date(9, …)` is the year 1909, not 9. Impossible days ("2026-02-31",
// which JS silently rolls into March) fail the same way.
function readInput(v: string): Date | null {
  if (!INPUT_SHAPE.test(v)) return null;
  const d = parseLocal(v);
  if (Number.isNaN(d.getTime())) return null;
  return (v.includes("T") ? toDateTimeInput(d) : toDateInput(d)) === v ? d : null;
}

// Whether a native date/datetime-local value is a moment this can work with.
// Exported for the one caller that has to remember the last START it could
// read: see useRangeStart.
export function isDateInputValue(v: string): boolean {
  return readInput(v) !== null;
}

// Move the end of a range along with its start, keeping the length. Every
// "van/tot" form in WLX was missing this: changing the DAY of an appointment
// left the end behind, so you were told the end time had to be after the start
// and had to retype the second field (Koen Segeren, 2026-09-02). Every calendar
// people already use does this, so nobody expects to have to.
//
// Only the START calls this. Editing the end on its own changes the length,
// which is the whole point of having two fields.
export function shiftEndWithStart(prevStart: string, nextStart: string, end: string): string {
  const from = readInput(prevStart);
  const to = readInput(nextStart);
  const current = readInput(end);
  // A half-typed or emptied field is not a request to move anything.
  if (!from || !to || !current) return end;

  if (!end.includes("T")) {
    // Whole days move by days, not by milliseconds: an hour gained or lost to
    // summer time must never land an all-day event on the day before.
    const days = Math.round((to.getTime() - from.getTime()) / 86_400_000);
    return days === 0 ? end : toDateInput(addDays(current, days));
  }
  // Timed events keep their duration in real elapsed time — the two ends are
  // instants, so a clock change between them takes care of itself.
  return toDateTimeInput(new Date(current.getTime() + (to.getTime() - from.getTime())));
}

export function startOfWeekMonday(d: Date): Date {
  const r = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const day = (r.getDay() + 6) % 7; // ma=0 … zo=6
  r.setDate(r.getDate() - day);
  return r;
}

export function endOfWeekMonday(d: Date): Date {
  const s = startOfWeekMonday(d);
  const e = new Date(s);
  e.setDate(e.getDate() + 6);
  e.setHours(23, 59, 59, 0);
  return e;
}

export function addDays(d: Date, n: number): Date {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

export function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

export function endOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59);
}

export function setTime(d: Date, h: number, m = 0): Date {
  const r = new Date(d);
  r.setHours(h, m, 0, 0);
  return r;
}

// ISO-8601 week number (weeks start Monday; week 1 contains the first Thursday).
// Logistics plans in week numbers ("week 32"), so the calendar shows them.
export function isoWeekNumber(d: Date): number {
  // Shift to the Thursday of this week, then count weeks from 1 January.
  const t = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  t.setDate(t.getDate() + 3 - ((t.getDay() + 6) % 7));
  const firstThursday = new Date(t.getFullYear(), 0, 4);
  firstThursday.setDate(firstThursday.getDate() + 3 - ((firstThursday.getDay() + 6) % 7));
  return 1 + Math.round((t.getTime() - firstThursday.getTime()) / (7 * 86_400_000));
}

export function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/**
 * Does an event that runs from `start` to `end` touch the calendar day `day`?
 *
 * `sameDay(start, day)` only ever matched the FIRST day, so a two-day trip or a
 * multi-day all-day item showed up on Monday and then vanished — the week looked
 * free on Tuesday while somebody was away. The end is exclusive at midnight:
 * Outlook models an all-day event as 00:00 to 00:00 the next day, and treating
 * that as touching the next day would put every single-day item on two days.
 */
export function overlapsDay(start: Date, end: Date, day: Date): boolean {
  const dayStart = new Date(day.getFullYear(), day.getMonth(), day.getDate());
  const dayEnd = new Date(dayStart.getTime() + 24 * 60 * 60 * 1000);
  return start < dayEnd && end > dayStart;
}

// ── Formatters (Europees) ────────────────────────────────────────────────────
const dfDate = new Intl.DateTimeFormat("nl-NL", {
  day: "2-digit",
  month: "short",
  year: "numeric",
  timeZone: TZ,
});
const dfDayDate = new Intl.DateTimeFormat("nl-NL", {
  weekday: "short",
  day: "2-digit",
  month: "short",
  timeZone: TZ,
});
const dfTime = new Intl.DateTimeFormat("nl-NL", {
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
  timeZone: TZ,
});
const dfMonthYear = new Intl.DateTimeFormat("nl-NL", {
  month: "long",
  year: "numeric",
  timeZone: TZ,
});

export const fmtDate = (d: Date) => dfDate.format(d);
export const fmtDayDate = (d: Date) => dfDayDate.format(d);
export const fmtTime = (d: Date) => dfTime.format(d);
export const fmtMonthYear = (d: Date) => dfMonthYear.format(d);

// "DD MMM YYYY, 14:08 CET" style for list display.
export function fmtDateTime(d: Date): string {
  return `${fmtDate(d)}, ${fmtTime(d)}`;
}
